import { CheckCircle, Loader2, AlertCircle, Download, FileText, Brain, CheckCheck, RefreshCw } from 'lucide-react';
import { TranscriptionStatus } from '@/types/transcription';
import { cn } from '@/lib/utils';

interface ProcessingStatusProps {
  status: TranscriptionStatus;
  errorMessage?: string | null;
}

interface Step {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  statuses: TranscriptionStatus[];
}

const STEPS: Step[] = [
  { 
    key: 'download', 
    label: 'Downloaden', 
    icon: Download,
    statuses: ['downloading'] 
  },
  { 
    key: 'convert', 
    label: 'Converteren', 
    icon: RefreshCw,
    statuses: ['converting'] 
  },
  { 
    key: 'transcribe', 
    label: 'Transcriberen', 
    icon: FileText,
    statuses: ['transcribing'] 
  },
  { 
    key: 'analyze', 
    label: 'Analyseren', 
    icon: Brain,
    statuses: ['analyzing', 'analyzing_attempt_1', 'analyzing_attempt_2', 'analyzing_attempt_3'] 
  },
  { 
    key: 'complete', 
    label: 'Voltooid', 
    icon: CheckCheck,
    statuses: ['completed'] 
  },
];

export const ProcessingStatus = ({ status, errorMessage }: ProcessingStatusProps) => {
  const getStepState = (step: Step): 'pending' | 'active' | 'completed' | 'failed' => {
    if (status === 'failed') return 'failed';
    if (status === 'completed') return 'completed';
    
    const stepIndex = STEPS.findIndex(s => s.key === step.key);
    const currentStepIndex = STEPS.findIndex(s => s.statuses.includes(status));
    
    // Handle 'processing' status as between download and convert
    if (status === 'processing') {
      if (stepIndex === 0) return 'completed'; // download done
      if (stepIndex === 1) return 'active'; // convert active
      return 'pending';
    }
    
    if (currentStepIndex === -1) {
      return stepIndex === 0 ? 'pending' : 'pending';
    }
    
    if (stepIndex < currentStepIndex) return 'completed';
    if (stepIndex === currentStepIndex) return 'active';
    return 'pending';
  };

  const getRetryNumber = (): number | null => {
    if (status === 'analyzing_attempt_2') return 2;
    if (status === 'analyzing_attempt_3') return 3;
    return null;
  };

  if (status === 'completed') {
    return null;
  }

  const retryNumber = getRetryNumber();

  const isQuotaError = errorMessage?.includes('OpenAI-limiet bereikt');
  const isFileTooLargeError = errorMessage?.includes('te groot') || errorMessage?.includes('FILE_TOO_LARGE');
  const isInvalidAudioError = errorMessage?.includes('could not be decoded') || errorMessage?.includes('INVALID_AUDIO');
  const isConversionError = errorMessage?.includes('CloudConvert') || errorMessage?.includes('conversie mislukt');

  return (
    <div className="space-y-4">
      {status === 'failed' && errorMessage && (
        <div className={cn(
          "flex items-start gap-3 p-4 rounded-lg border",
          isQuotaError 
            ? "bg-amber-500/10 border-amber-500/30" 
            : (isFileTooLargeError || isInvalidAudioError || isConversionError)
            ? "bg-orange-500/10 border-orange-500/30"
            : "bg-destructive/10 border-destructive/30"
        )}>
          <AlertCircle className={cn(
            "h-5 w-5 shrink-0 mt-0.5",
            isQuotaError ? "text-amber-500" : (isFileTooLargeError || isInvalidAudioError || isConversionError) ? "text-orange-500" : "text-destructive"
          )} />
          <div className="flex-1">
            <p className={cn(
              "font-medium",
              isQuotaError ? "text-amber-600" : (isFileTooLargeError || isInvalidAudioError || isConversionError) ? "text-orange-600" : "text-destructive"
            )}>
              {isQuotaError 
                ? 'OpenAI-Limiet Bereikt' 
                : isFileTooLargeError 
                ? 'Bestand Te Groot' 
                : isInvalidAudioError 
                ? 'Ongeldig Audioformaat' 
                : isConversionError
                ? 'Conversie Mislukt'
                : 'Verwerking mislukt'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {isQuotaError 
                ? 'De beheerder is op de hoogte gesteld en zal het tegoed aanvullen.'
                : errorMessage
              }
            </p>
            {(isFileTooLargeError || isInvalidAudioError) && !isConversionError && (
              <div className="mt-3 p-3 bg-muted/50 rounded-md">
                <p className="text-xs font-medium text-foreground mb-1">Oplossing:</p>
                <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Converteer het bestand naar MP3 met lagere bitrate (128kbps)</li>
                  <li>Gebruik een online converter zoals <a href="https://cloudconvert.com/m4a-to-mp3" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">CloudConvert</a></li>
                  <li>Of splits het bestand in kortere segmenten (&lt;20 minuten)</li>
                </ol>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        {STEPS.map((step, index) => {
          const state = getStepState(step);
          const StepIcon = step.icon;
          
          return (
            <div key={step.key} className="flex items-center flex-1">
              <div className="flex flex-col items-center gap-1 flex-1">
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    state === 'completed' && "bg-green-500/20 text-green-500",
                    state === 'active' && "bg-primary/20 text-primary",
                    state === 'pending' && "bg-muted text-muted-foreground",
                    state === 'failed' && "bg-destructive/20 text-destructive"
                  )}
                >
                  {state === 'completed' ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : state === 'active' ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : state === 'failed' ? (
                    <AlertCircle className="h-5 w-5" />
                  ) : (
                    <StepIcon className="h-5 w-5" />
                  )}
                </div>
                <span className={cn(
                  "text-xs text-center",
                  state === 'active' && "font-medium text-primary",
                  state === 'completed' && "text-green-500",
                  state === 'failed' && "text-destructive",
                  state === 'pending' && "text-muted-foreground"
                )}>
                  {step.label}
                  {step.key === 'analyze' && retryNumber && state === 'active' && (
                    <span className="block text-[10px]">Poging {retryNumber}/3</span>
                  )}
                </span>
              </div>
              
              {index < STEPS.length - 1 && (
                <div className={cn(
                  "h-0.5 flex-1 mx-2",
                  getStepState(STEPS[index + 1]) !== 'pending' 
                    ? "bg-green-500" 
                    : "bg-muted"
                )} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
